// Image caching utility
class ImageCache {
  constructor(maxSize = 50) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  // Add image to cache
  set(url, imageData) {
    if (this.cache.size >= this.maxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(url, {
      data: imageData,
      timestamp: Date.now()
    });
  }

  // Get image from cache
  get(url) {
    const entry = this.cache.get(url);
    if (entry) {
      // Check if cache entry is still valid (24 hours)
      const isValid = Date.now() - entry.timestamp < 24 * 60 * 60 * 1000;
      if (isValid) {
        return entry.data;
      } else {
        this.cache.delete(url);
      }
    }
    return null;
  }

  // Check if image exists in cache
  has(url) {
    return this.cache.has(url);
  }

  // Clear cache
  clear() {
    this.cache.clear();
  }

  // Get cache size
  size() {
    return this.cache.size;
  }

  // Preload image and add to cache
  async preload(url) {
    if (this.has(url)) {
      return this.get(url);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.set(url, img);
        resolve(img);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  // Batch preload multiple images
  async preloadBatch(urls) {
    const promises = urls.map(url => this.preload(url).catch(() => null));
    return Promise.all(promises);
  }
}

// Create global image cache instance
export const imageCache = new ImageCache();

// Utility functions
export const preloadProductImages = async (products) => {
  const imageUrls = products
    .filter(product => product.images && product.images.length > 0)
    .map(product => {
      const imageUrl = product.images[0];
      return imageUrl.startsWith('http') ? imageUrl : `/api${imageUrl}`;
    });

  return imageCache.preloadBatch(imageUrls);
};

export const getOptimizedImageUrl = (originalUrl, options = {}) => {
  if (!originalUrl) return null;
  
  const {
    width,
    height,
    quality = 80,
    format = 'webp'
  } = options;
  
  // If it's already an external URL, return as is
  if (originalUrl.startsWith('http') && !originalUrl.includes('localhost')) {
    return originalUrl;
  }
  
  // For local images, add optimization parameters
  const baseUrl = originalUrl.startsWith('/') ? 
    `/api${originalUrl}` : 
    originalUrl;
  
  try {
    const url = new URL(baseUrl);
    if (width) url.searchParams.set('w', width);
    if (height) url.searchParams.set('h', height);
    url.searchParams.set('q', quality);
    url.searchParams.set('f', format);
    
    return url.toString();
  } catch {
    return originalUrl;
  }
};

